import { TableFilter } from "@neonumy/ant-design";
import { clientEntities } from "@neonumy/api-clients";
import { useGetWindowHeight } from "@neonumy/neonumy-ui";
import { Badge, Col, Row, Space, Typography } from "antd";
import Text from "antd/es/typography/Text";
import { DATE_FORMAT, DATE_FORMAT_API } from "config/settings";
import dayjs from "dayjs";
import { LIST_CLIENTS_PAGINATED_SELECT } from "queries/entities/clients";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFileExport } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import { formatNumberWithSpaces } from "utils/formatNumberWithSpaces";
import { customToFixed } from "utils/toFixedNumber";
import SendEmail from "views/Clients/Payments/components/SendEmail";
import TooltipButton from "views/Documents/components/TooltipButton";
import useGeneralApi from "views/Settings/General/hooks/useGeneralApi";
import usePaymentsApi from "../../hooks/usePaymentsApi";
import CreateUpdatePayment from "./CreateUpdatePayment";
import RemoveButton from "./RemoveButton";
import useCoreStore from "views/hooks/useCoreStore";

export default function PaymentsTable() {
  const windowHeight = useGetWindowHeight();
  const { companyId }: RouteParams = useParams();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { listDefaultPaymentMethods, paymentMethodsOptions } = useGeneralApi();

  const PAGINATION_INIT = {
    offset: 0,
    limit: 10,
  };

  const [selectedRows, setSelectedRows] = useState({
    selectedRowInfo: [],
    selectedRowKeys: [],
  });

  const [filters, setFilters] = useState<any>({
    group_CompanyId: companyId,
    date_Lte: undefined,
    date_Gte: undefined,
    document_Reference_Icontains: undefined,
    group_ClientId: undefined,
    reference_Icontains: undefined,
    group_PaymentMethodId: undefined,
  });

  const { selectedRowKeys } = selectedRows;
  const { listPayments, dataSource, totalCount, refetch, loading, getPaymentsXlsx } =
    usePaymentsApi();

  useEffect(() => {
    if (companyId) {
      listPayments({
        variables: {
          ...PAGINATION_INIT,
          filter: { group_CompanyId: companyId },
        },
      });
      listDefaultPaymentMethods();
    }
  }, [companyId]);

  const getTableColumns = () => [
    {
      title: t("Client"),
      dataIndex: "client",
      key: "client",
      width: "15%",
      filterType: {
        type: "AsyncSelectFilter",
        varName: "group_ClientId_In",
        asyncSelectSettings: {
          translations: {
            placeholder: t("Select an option"),
            loadingMessage: t("Loading"),
            noOptionsMessage: t("No options"),
            button: {
              search: t("Search"),
              reset: t("Reset"),
            },
          },
          hasQueryFilter: true,
          isMulti: true,
          autoConfirm: false,
          queryOptions: {
            client: clientEntities,
            dataName: "listEntities",
            query: LIST_CLIENTS_PAGINATED_SELECT,
            variables: {
              filter: {
                companyId,
                entityType: "CLIENT",
                search: "",
              },
              limit: 20,
              orderBy: ["name"],
            },
          },
        },
      },
      render: (_text: unknown, record: Record<string, any>) =>
        record?.group?.client && (
          <span>{`${record?.group?.client?.code} - ${record?.group?.client?.name}`}</span>
        ),
    },
    {
      key: "reference",
      title: t("Invoice nr"),
      filterType: {
        type: "SearchFilter",
        varName: "document_Reference_Icontains",
      },
      render: (_text: unknown, record: Record<string, any>) => (
        <span>{`${record?.group?.saleDocuments[0]?.reference || ""}`}</span>
      ),
    },
    {
      title: t("Reference"),
      dataIndex: "reference",
      filterType: {
        type: "SearchFilter",
        varName: "reference_Icontains",
      },
      render: (value: unknown) => {
        return (
          <Space size="small" direction="vertical">
            <Text>{value as React.ReactNode}</Text>
          </Space>
        );
      },
    },
    {
      key: "amount",
      title: t("Amount"),
      align: "right",
      render: (_text: unknown, record: Record<string, any>) => {
        const decimalPlaces = record?.group?.currency?.decimalPlaces;
        const currencyCode = record?.group?.currency?.code;
        return (
          record?.amount && (
            <span>{`${formatNumberWithSpaces(customToFixed(Number(record?.amount), decimalPlaces))} ${currencyCode}`}</span>
          )
        );
      },
    },
    {
      key: "paymentMethod",
      title: t("Payment Method"),
      filterType: {
        type: "SelectFilter",
        varName: "group_PaymentMethodId",
        options: paymentMethodsOptions,
      },
      render: (_text: unknown, record: Record<string, any>) =>
        record?.paymentMethod && <span>{record?.paymentMethod?.name}</span>,
    },
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      filterType: {
        type: "DateFilter",
        maxDate: dayjs().endOf("day"),
        varName: ["date_Gte", "date_Lte"],
      },
      render: (_text: unknown, record: Record<string, any>) =>
        record?.date && <span>{dayjs(record?.date).format(DATE_FORMAT)}</span>,
    },
    {
      dataIndex: t("Action"),
      key: "action",
      title: "Action",
      width: "150px",
      align: "center",
      fixed: "right",
      render: (_text: unknown, record: Record<string, any>) => {
        return (
          <Space size="small" direction="horizontal" className="centered">
            <SendEmail
              id={record.id}
              email={record?.group?.client?.email}
              reference={record?.group?.saleDocuments[0]?.reference}
              amount={record?.amount}
              clientName={`${record?.group?.client?.code} - ${record?.group?.client?.name}`}
              currency={record?.group?.currency?.code}
              decimalPlaces={record?.group?.currency?.decimalPlaces}
            />
            <CreateUpdatePayment id={record.id} refetch={refetch} />
            <RemoveButton id={record.id} refetch={refetch} />
          </Space>
        );
      },
    },
  ];

  const COLUMNS = getTableColumns();

  const handlePagination = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(page);
    const refetchFilters = { ...filters };

    if (refetchFilters.date_Lte) {
      refetchFilters.date_Lte = dayjs(refetchFilters.date_Lte).format(DATE_FORMAT_API);
    }

    if (refetchFilters.date_Gte) {
      refetchFilters.date_Gte = dayjs(refetchFilters.date_Gte).format(DATE_FORMAT_API);
    }
    refetch({
      filter: { group_CompanyId: companyId, ...refetchFilters },
      limit: size,
      offset: size * (page - 1),
    });
  };

  const resetSelectedRows = () => {
    setSelectedRows({
      selectedRowInfo: [],
      selectedRowKeys: [],
    });
  };

  const handleExportDocuments = async () => {
    const refetchFilters = { ...filters };

    if (refetchFilters.date_Lte) {
      refetchFilters.date_Lte = dayjs(refetchFilters.date_Lte).format(DATE_FORMAT_API);
    }

    if (refetchFilters.date_Gte) {
      refetchFilters.date_Gte = dayjs(refetchFilters.date_Gte).format(DATE_FORMAT_API);
    }
    const response = await getPaymentsXlsx({
      variables: {
        filter: {
          id_In: selectedRowKeys?.length > 0 ? selectedRowKeys : undefined,
          ...refetchFilters,
        },
      },
    });

    const url = response?.data?.getPaymentsXlsx;

    if (url) {
      resetSelectedRows();
      downloadFile(url);
    }
  };

  const downloadFile = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const rowSelection = {
    selectedRowKeys,
    columnWidth: "4%",
    onChange: (selectedRowKeys: any, selectedRows: any) => {
      if (selectedRows.length > 1) {
        const currencies = selectedRows.map((row: any) => row?.group?.currency?.code);
        const uniqueCurrencies = [...new Set(currencies)];

        if (uniqueCurrencies.length > 1) {
          const lastSelectedRow = selectedRows[selectedRows.length - 1];
          const targetCurrency = lastSelectedRow?.group?.currency?.code;

          const filteredRows = selectedRows.filter(
            (row: any) => row?.group?.currency?.code === targetCurrency,
          );
          const filteredKeys = filteredRows.map((row: any) => row.key);

          setSelectedRows({
            selectedRowInfo: filteredRows,
            selectedRowKeys: filteredKeys,
          });
          return;
        }
      }

      setSelectedRows({
        selectedRowInfo: selectedRows,
        selectedRowKeys: selectedRowKeys,
      });
    },
    getCheckboxProps: (record: any) => {
      if (selectedRows.selectedRowInfo.length > 0) {
        const selectedCurrency = (selectedRows.selectedRowInfo as any)[0]?.group?.currency?.code;
        const recordCurrency = record?.group?.currency?.code;

        return {
          disabled: selectedCurrency !== recordCurrency,
        };
      }

      return {};
    },
  };

  const handleResetFilter = (resetedFilter: Record<string, unknown>, confirm?: any) => {
    confirm?.();
    setCurrentPage(1);
    setFilters((filters: Record<string, unknown>) => ({
      ...filters,
      ...resetedFilter,
    }));
    refetch({
      ...filters,
      ...resetedFilter,
    });
  };

  const handleSearchFilters = async (confirm: () => void) => {
    confirm();
    setCurrentPage(1);
    resetSelectedRows();

    if (totalCount !== undefined) {
      const refetchFilters = { ...filters };

      if (refetchFilters.date_Lte) {
        refetchFilters.date_Lte = dayjs(refetchFilters.date_Lte).format(DATE_FORMAT_API);
      }

      if (refetchFilters.date_Gte) {
        refetchFilters.date_Gte = dayjs(refetchFilters.date_Gte).format(DATE_FORMAT_API);
      }

      refetch({
        ...PAGINATION_INIT,
        filter: refetchFilters,
        limit: pageSize,
      });
    }
  };

  const currency = useCoreStore(state => state.currency);
  const decimalPlaces = currency?.decimalPlaces || 2;

  const selectedCount = selectedRowKeys?.length || 0;
  const hasSelection = selectedCount > 0;

  const totalAmount =
    selectedRows?.selectedRowInfo?.reduce((sum: number, item: any) => {
      return sum + Number(item?.amount || 0);
    }, 0) || 0;

  const selectedCurrency =
    selectedRows?.selectedRowInfo?.length > 0
      ? (selectedRows.selectedRowInfo as any)[0]?.group?.currency
      : currency;

  const selectedDecimalCases = selectedCurrency?.decimalPlaces || decimalPlaces;
  const formattedTotal = formatNumberWithSpaces(customToFixed(totalAmount, selectedDecimalCases));

  return (
    <>
      <Typography.Title className="documents-title" level={4}>
        {t("Payments")}
      </Typography.Title>
      <div className="table-header-actions">
        <div className="selection-info">
          {hasSelection ? (
            <div className="selected-count-container">
              <Badge className="badge-selected-count" count={selectedCount} />
              <span className="selected-count-text">{t("documents selected")}</span>
            </div>
          ) : (
            <div className="no-selection-text">{t("None selected")}</div>
          )}

          {hasSelection && totalAmount > 0 && (
            <div className="total-currency-display" style={{ marginLeft: "12px" }}>
              <span className="total-currency-text">
                {t("Total")}: {formattedTotal} {selectedCurrency?.code || currency?.code}
              </span>
            </div>
          )}
        </div>
        <Space className="actions-container">
          <Row justify="start" gutter={[4, 16]}>
            <Col>
              <TooltipButton
                placement="left"
                title={hasSelection ? t("Export selected documents") : t("Export all documents")}
                handleOnClick={handleExportDocuments}
                icon={<FaFileExport />}
              />
            </Col>
          </Row>
        </Space>
      </div>
      <TableFilter
        className="antd-newrai-table"
        scroll={{ y: windowHeight - 350 }}
        loading={loading}
        columns={COLUMNS}
        dataSource={dataSource}
        pagination={{
          onChange: handlePagination,
          showSizeChanger: true,
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showTotal: total => t("Total table items", { count: total }),
        }}
        rowSelection={rowSelection}
        defaultSearchFilter={handleSearchFilters}
        defaultResetFilter={handleResetFilter}
        defaultFiltersObject={filters}
        defaultFilterSet={setFilters}
        debounced={true}
      />
      <CreateUpdatePayment refetch={refetch} />
    </>
  );
}
